import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { useToast } from "../../components/ui/use-toast";
import { useFetch } from "../../lib/useFetch";
import { cn } from "../../lib/utils";
import { extractFromMultiName } from "../Locations/utils";

/**
 * Link an imported booking to its yacht & base. Candidates are ranked by the
 * API: yacht AND operator matching the deal first, then yacht name only, then
 * similar names. Several confident rows means the operator runs more than one
 * boat with that name — the reason those bookings weren't auto-linked.
 */

export type YachtLinkCandidate = {
  yachtId: string;
  yachtName: string;
  operatorId: string | null;
  operatorName: string | null;
  locationId: string | null;
  locationNames: string | null;
  boardingAtBase: number;
  confidence: "confident" | "name_match" | "similar";
};

type CandidatesResponse = {
  dealYacht: string;
  supplier: string;
  linkedYachtId: string | null;
  linkMethod: string | null;
  candidates: YachtLinkCandidate[];
};

const CONFIDENCE: Record<YachtLinkCandidate["confidence"], { label: string; className: string }> = {
  confident: { label: "yacht + operator match", className: "bg-green-100 text-green-800" },
  name_match: { label: "yacht name match", className: "bg-amber-100 text-amber-800" },
  similar: { label: "similar name", className: "bg-slate-100 text-slate-600" },
};

export function LinkYachtDialog({
  bookingId,
  onClose,
  onLinked,
}: {
  bookingId: string;
  onClose: () => void;
  onLinked: () => void;
}) {
  const { fetch } = useFetch();
  const { toast } = useToast();
  const [q, setQ] = useState("");
  const [data, setData] = useState<CandidatesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [posting, setPosting] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(
      () => {
        setLoading(true);
        setError(null);
        fetch<{ data: CandidatesResponse }>(
          `/api/bookings/${bookingId}/yacht-link-candidates${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ""}`,
        )
          .then((r) => setData(r.data))
          .catch((e: any) => setError(e?.message ?? "Couldn't load yachts."))
          .finally(() => setLoading(false));
      },
      q ? 300 : 0,
    );
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, q]);

  const link = (c: YachtLinkCandidate) => {
    setPosting(c.yachtId);
    setError(null);
    fetch(`/api/bookings/${bookingId}/link-yacht`, "POST", { yachtId: c.yachtId })
      .then(() => {
        toast({ title: "Yacht linked", description: [c.yachtName, c.operatorName].filter(Boolean).join(" · ") });
        onLinked();
      })
      .catch((e: any) => {
        setError(e?.message ?? "Couldn't link the yacht.");
        setPosting(null);
      });
  };

  const confidentCount = (data?.candidates ?? []).filter((c) => c.confidence === "confident").length;

  return (
    <Dialog open onOpenChange={(o) => !o && !posting && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Link yacht & base</DialogTitle>
        </DialogHeader>
        {data && (
          <p className="text-sm text-slate-500">
            The deal says <span className="font-medium text-slate-700">{data.dealYacht || "no yacht"}</span>
            {data.supplier && (
              <>
                {" "}
                with <span className="font-medium text-slate-700">{data.supplier}</span>
              </>
            )}
            .
          </p>
        )}
        {confidentCount > 1 && (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
            {confidentCount} boats match both the yacht and the operator — check the base before linking.
          </p>
        )}
        {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search another yacht name…"
          className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-400"
        />
        <div className="space-y-2">
          {loading && !data ? (
            <p className="text-sm text-slate-400">Finding yachts…</p>
          ) : !data?.candidates.length ? (
            <p className="text-sm text-slate-400">No yachts match — try a different name.</p>
          ) : (
            data.candidates.map((c) => (
              <div
                key={c.yachtId}
                className={cn(
                  "flex items-center justify-between gap-3 rounded-lg border px-3 py-2",
                  data.linkedYachtId === c.yachtId && "border-green-400 bg-green-50/50",
                )}
              >
                <div className="min-w-0">
                  <p className="font-medium">
                    {c.yachtName}
                    <span className={cn("ml-2 rounded-full px-2 py-0.5 text-[11px] font-medium", CONFIDENCE[c.confidence].className)}>
                      {CONFIDENCE[c.confidence].label}
                    </span>
                  </p>
                  <p className="text-xs text-slate-500">
                    {[c.operatorName ?? "no operator", c.locationNames ? extractFromMultiName(c.locationNames) : "no base"].join(" · ")}
                    {" · "}
                    {c.boardingAtBase > 0 ? (
                      <span className="text-green-700">boarding info at this base</span>
                    ) : (
                      <span className="text-slate-400">no boarding info at this base</span>
                    )}
                  </p>
                </div>
                {data.linkedYachtId === c.yachtId ? (
                  <span className="text-xs font-medium text-green-700">Linked</span>
                ) : (
                  <Button size="sm" disabled={!!posting} onClick={() => link(c)}>
                    {posting === c.yachtId ? "Linking…" : "Link"}
                  </Button>
                )}
              </div>
            ))
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={!!posting} onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
